import { useState, useEffect, useRef } from 'react'
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { CATEGORY_MAP, type Category } from '@/lib/constants'
import { useRecords } from '@/contexts/record-context'
import { formatDuration } from '@/lib/utils'
import { AlertTriangle, Play, Square, Trash2 } from 'lucide-react'

export function RecoveryModal() {
  const { activeTimer, stopTimer, discardTimer, startTimer } = useRecords()
  const [show, setShow] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const dismissedRef = useRef(false)
  const pageLoadTimeRef = useRef(Date.now())

  useEffect(() => {
    if (activeTimer && !dismissedRef.current) {
      // Check if timer was started before page load (restored from storage)
      const isRestoredTimer = activeTimer.startTime < pageLoadTimeRef.current
      if (isRestoredTimer) {
        setShow(true)
        const interval = setInterval(() => {
          setElapsed(Math.floor((Date.now() - activeTimer.startTime) / 1000))
        }, 1000)
        return () => clearInterval(interval)
      }
    } else if (!activeTimer) {
      // Timer was fully stopped, reset dismissed flag for next time
      dismissedRef.current = false
    }
  }, [activeTimer])

  if (!show || !activeTimer) return null

  const config = CATEGORY_MAP[activeTimer.category as Category]
  if (!config) return null

  const handleContinue = () => {
    dismissedRef.current = true
    setShow(false)
  }

  const handleStop = () => {
    stopTimer()
    setShow(false)
  }

  const handleDiscard = () => {
    discardTimer()
    setShow(false)
  }

  const Icon = config.icon

  return (
    <Dialog open onClose={() => {}}>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          发现未结束的记录
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/50 mb-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: config.bgColor }}
          >
            <Icon className="h-6 w-6" style={{ color: config.color }} />
          </div>
          <div>
            <p className="font-semibold" style={{ color: config.color }}>
              {config.label}
            </p>
            <p className="text-sm text-muted-foreground">
              已计时 {formatDuration(elapsed)}
            </p>
          </div>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          上次离开时有一个正在进行的记录，您想怎么处理？
        </p>

        <div className="space-y-2">
          <Button className="w-full gap-2" onClick={handleContinue}>
            <Play className="h-4 w-4" />
            继续记录
          </Button>
          <Button className="w-full gap-2" variant="outline" onClick={handleStop}>
            <Square className="h-4 w-4" />
            结束记录
          </Button>
          <Button className="w-full gap-2" variant="ghost" onClick={handleDiscard}>
            <Trash2 className="h-4 w-4" />
            放弃记录
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
